import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { inrAmount } from '../../../../utils/formatters/inrAmount';
import { useUnitPreference } from '../../DashboardModules/AuditedFinancialsVisualisation/useUnitPreference';
import {
    AUDITED_UNIT_DIVISOR,
    AUDITED_UNIT_OPTIONS,
    AUDITED_UNIT_SHORT_LABEL,
} from '../../DashboardModules/AuditedFinancialsVisualisation/auditedStatementLayout';

// Reuses the Audited/Provisional Financials units preference rather than a second
// copy of the same localStorage-lazy-init + persist-effect + divisor table — this drawer's own
// key (below) keeps its selection independent of those surfaces, matching that hook's own
// per-surface-key convention.
export const UNIT_OPTIONS = AUDITED_UNIT_OPTIONS;
const RECONCILIATIONS_UNIT_STORAGE_KEY = 'aica.reconciliations.unit';

/** Client-side only — units never trigger a refetch (D11, A-65). */
export function formatAmountInUnit(value: number, unit: string): string {
    const divisor = AUDITED_UNIT_DIVISOR[unit] ?? 1;
    if (divisor === 1) return inrAmount(value);
    const divided = value / divisor;
    // Grouped en-IN like the unscaled branch above (D11) — otherwise the same figure reads
    // `12,800.00` on the Financials tab and `12800.00` in the drawer that tab's pill opens.
    const formatted = Math.abs(divided).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return divided < 0 ? `(${formatted})` : formatted;
}

/** Prose (tooltip) variant of the above: a scaled figure carries no unit of its own, and a
 *  sentence has no column header to borrow one from. */
export function formatAmountWithUnit(value: number, unit: string): string {
    const formatted = formatAmountInUnit(value, unit);
    const label = AUDITED_UNIT_SHORT_LABEL[unit];
    return label && label !== '₹' ? `₹${formatted} ${label}` : `₹${formatted}`;
}

interface UnitsContextValue {
    unit: string;
    setUnit: (unit: string) => void;
}

const UnitsContext = createContext<UnitsContextValue>({ unit: AUDITED_UNIT_OPTIONS[0], setUnit: () => {} });

export function UnitsProvider({ children }: Readonly<{ children: ReactNode }>) {
    const [unit, setUnit] = useUnitPreference(RECONCILIATIONS_UNIT_STORAGE_KEY);
    const value = useMemo(() => ({ unit, setUnit }), [unit, setUnit]);
    return <UnitsContext.Provider value={value}>{children}</UnitsContext.Provider>;
}

export function useUnits(): UnitsContextValue {
    return useContext(UnitsContext);
}
